import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Save, RefreshCw, Search, Globe, FileText, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SEOManagement = () => {
  const { token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [seoData, setSeoData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const pageLabels = {
    servicos: { pt: 'Serviços', en: 'Services', icon: '🛠️' },
    regioes: { pt: 'Regiões', en: 'Regions', icon: '📍' },
    sobre: { pt: 'Sobre', en: 'About', icon: 'ℹ️' },
    blog: { pt: 'Blog', en: 'Blog', icon: '📝' },
    contacto: { pt: 'Contacto', en: 'Contact', icon: '📧' }
  };

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin/login');
      return;
    }
    fetchSeoData();
  }, [isAuthenticated, navigate]);

  const fetchSeoData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/seo`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSeoData(response.data);
    } catch (error) {
      console.error('Error fetching SEO data:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os dados de SEO',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (page, field, value) => {
    setSeoData(prev => 
      prev.map(item => 
        item.page === page ? { ...item, [field]: value } : item
      )
    );
  };

  const handleSave = async (page) => {
    const pageData = seoData.find(item => item.page === page);
    if (!pageData) return;

    setSaving(prev => ({ ...prev, [page]: true }));

    try {
      await axios.put(`${BACKEND_URL}/api/admin/seo/${page}`, {
        title_pt: pageData.title_pt,
        title_en: pageData.title_en,
        description_pt: pageData.description_pt,
        description_en: pageData.description_en
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast({
        title: 'Sucesso!',
        description: `SEO da página "${pageLabels[page]?.pt || page}" atualizado com sucesso.`
      });
    } catch (error) {
      console.error('Error saving SEO:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível salvar os dados de SEO',
        variant: 'destructive'
      });
    } finally {
      setSaving(prev => ({ ...prev, [page]: false }));
    }
  };

  const handleReset = async (page) => {
    if (!window.confirm(`Tem certeza que deseja resetar o SEO da página "${pageLabels[page]?.pt || page}" para os valores padrão?`)) {
      return;
    }

    setSaving(prev => ({ ...prev, [page]: true }));

    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/seo/${page}/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSeoData(prev => 
        prev.map(item => 
          item.page === page ? response.data.data : item
        )
      );

      toast({
        title: 'Sucesso!',
        description: `SEO da página "${pageLabels[page]?.pt || page}" foi resetado para os valores padrão.`
      });
    } catch (error) {
      console.error('Error resetting SEO:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível resetar os dados de SEO',
        variant: 'destructive'
      });
    } finally {
      setSaving(prev => ({ ...prev, [page]: false }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-600">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/admin/dashboard')}
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Search className="h-6 w-6" />
                  Gestão de SEO
                </h1>
                <p className="text-sm text-gray-500">Configure títulos e descrições para motores de busca</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {seoData.map((item) => (
            <Card key={item.page} className="overflow-hidden">
              <CardHeader className="bg-gray-50 border-b">
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <span className="text-2xl">{pageLabels[item.page]?.icon}</span>
                    <span>/{item.page}</span>
                    <span className="text-gray-400 font-normal">({pageLabels[item.page]?.pt})</span>
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleReset(item.page)}
                      disabled={saving[item.page]}
                    >
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Reset
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleSave(item.page)}
                      disabled={saving[item.page]}
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {saving[item.page] ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Portuguese */}
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <span className="text-lg">🇵🇹</span> Português
                  </div>
                  <div className="grid gap-4 pl-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <FileText className="h-4 w-4 inline mr-1" />
                        Meta Title
                      </label>
                      <Input
                        value={item.title_pt || ''}
                        onChange={(e) => handleChange(item.page, 'title_pt', e.target.value)}
                        placeholder="Título da página para SEO"
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {(item.title_pt || '').length}/60 caracteres (recomendado)
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Globe className="h-4 w-4 inline mr-1" />
                        Meta Description
                      </label>
                      <Textarea
                        value={item.description_pt || ''}
                        onChange={(e) => handleChange(item.page, 'description_pt', e.target.value)}
                        placeholder="Descrição da página para SEO"
                        rows={2}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {(item.description_pt || '').length}/160 caracteres (recomendado)
                      </p>
                    </div>
                  </div>
                </div>

                {/* English */}
                <div className="space-y-4 border-t pt-6">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <span className="text-lg">🇬🇧</span> English
                  </div>
                  <div className="grid gap-4 pl-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <FileText className="h-4 w-4 inline mr-1" />
                        Meta Title
                      </label>
                      <Input
                        value={item.title_en || ''}
                        onChange={(e) => handleChange(item.page, 'title_en', e.target.value)}
                        placeholder="Page title for SEO"
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {(item.title_en || '').length}/60 characters (recommended)
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        <Globe className="h-4 w-4 inline mr-1" />
                        Meta Description
                      </label>
                      <Textarea
                        value={item.description_en || ''}
                        onChange={(e) => handleChange(item.page, 'description_en', e.target.value)}
                        placeholder="Page description for SEO"
                        rows={2}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {(item.description_en || '').length}/160 characters (recommended)
                      </p>
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="border-t pt-6">
                  <p className="text-sm font-medium text-gray-700 mb-3">📱 Pré-visualização no Google:</p>
                  <div className="bg-white border rounded-lg p-4 max-w-xl">
                    <div className="text-blue-600 text-lg hover:underline cursor-pointer truncate">
                      {item.title_pt || 'Título da página'}
                    </div>
                    <div className="text-green-700 text-sm">
                      yourbrokerportugal.com/{item.page}
                    </div>
                    <div className="text-gray-600 text-sm line-clamp-2">
                      {item.description_pt || 'Descrição da página aparece aqui...'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
};

export default SEOManagement;
